/**
 * Unified Checkout Handler
 *
 * Provides a standardized way to handle Stripe checkout across the app:
 * - Mobile browsers (iOS/Android): Same-page redirect to avoid popup blocking
 * - Desktop browsers: Opens checkout in new tab
 * - Monitors checkout window
 * - Receives PostMessage from success/error pages
 * - Auto-closes checkout tab
 * - Refreshes subscription data
 * - Shows appropriate toasts
 */

import { isMobileDevice } from '@/utils/device-detection';

export interface CheckoutOptions {
  /**
   * Stripe checkout URL from create-checkout edge function
   */
  checkoutUrl: string;

  /**
   * Callback when payment succeeds (can be async)
   */
  onSuccess?: () => void | Promise<void>;

  /**
   * Callback when payment fails or is canceled (can be async)
   */
  onError?: (error?: string) => void | Promise<void>;

  /**
   * Callback when checkout window closes (manual or auto) (can be async)
   */
  onClose?: () => void | Promise<void>;

  /**
   * Show toast notifications (default: true)
   */
  showToasts?: boolean;
}

export interface CheckoutResult {
  /**
   * Stop monitoring and cleanup
   */
  cleanup: () => void;

  /**
   * Reference to the checkout window (if opened successfully)
   */
  checkoutWindow: Window | null;
}

/**
 * Opens Stripe checkout in a new tab and handles the complete flow
 */
export function handleCheckout(options: CheckoutOptions): CheckoutResult {
  const {
    checkoutUrl,
    onSuccess,
    onError,
    onClose,
    showToasts = true,
  } = options;

  // MOBILE DETECTION: Use same-page redirect for mobile browsers to avoid popup blocking
  if (isMobileDevice()) {
    console.log('[CheckoutHandler] Mobile device detected - using same-page redirect');

    // Store checkout options in sessionStorage for when we return from Stripe
    sessionStorage.setItem('checkout_pending', 'true');
    sessionStorage.setItem('checkout_return_url', window.location.href);

    // Store callbacks as flags (we'll handle them in the success/error pages)
    if (onSuccess) {
      sessionStorage.setItem('checkout_has_success_callback', 'true');
    }
    if (onError) {
      sessionStorage.setItem('checkout_has_error_callback', 'true');
    }
    if (onClose) {
      sessionStorage.setItem('checkout_has_close_callback', 'true');
    }

    // Redirect to Stripe checkout in the same window
    window.location.href = checkoutUrl;

    // Return no-op cleanup since we're leaving the page
    return {
      cleanup: () => {
        console.log('[CheckoutHandler] Mobile redirect - no cleanup needed');
      },
      checkoutWindow: null,
    };
  }

  // DESKTOP: Use popup/new tab approach
  console.log('[CheckoutHandler] Desktop browser detected - using popup window');

  let checkoutWindow: Window | null = null;
  let windowCheckInterval: NodeJS.Timeout | null = null;
  let messageListener: ((event: MessageEvent) => void) | null = null;
  let isComplete = false;

  /**
   * Cleanup function - stops all monitoring and removes listeners
   */
  const cleanup = () => {
    console.log('[CheckoutHandler] Cleaning up checkout handler');

    // Clear window check interval
    if (windowCheckInterval) {
      clearInterval(windowCheckInterval);
      windowCheckInterval = null;
    }

    // Remove message listener
    if (messageListener) {
      window.removeEventListener('message', messageListener);
      messageListener = null;
    }

    // Close checkout window if still open
    if (checkoutWindow && !checkoutWindow.closed) {
      try {
        checkoutWindow.close();
      } catch (error) {
        console.warn('[CheckoutHandler] Failed to close checkout window:', error);
      }
    }

    checkoutWindow = null;
  };

  /**
   * Handle successful payment
   */
  const handleSuccess = async () => {
    if (isComplete) return; // Prevent duplicate handling
    isComplete = true;

    console.log('[CheckoutHandler] ✅ Payment successful');

    // Close checkout window
    if (checkoutWindow && !checkoutWindow.closed) {
      checkoutWindow.close();
    }

    // Cleanup
    cleanup();

    // Call success callback with error handling
    if (onSuccess) {
      try {
        await onSuccess();
      } catch (error) {
        console.error('[CheckoutHandler] Error in onSuccess callback:', error);
      }
    }
  };

  /**
   * Handle payment error or cancellation
   */
  const handleError = async (errorMessage?: string) => {
    if (isComplete) return; // Prevent duplicate handling
    isComplete = true;

    console.log('[CheckoutHandler] ❌ Payment failed or canceled:', errorMessage);

    // Close checkout window
    if (checkoutWindow && !checkoutWindow.closed) {
      checkoutWindow.close();
    }

    // Cleanup
    cleanup();

    // Call error callback with error handling
    if (onError) {
      try {
        await onError(errorMessage);
      } catch (error) {
        console.error('[CheckoutHandler] Error in onError callback:', error);
      }
    }
  };

  /**
   * Handle window close (manual close by user)
   */
  const handleWindowClose = async () => {
    if (isComplete) return; // Already handled via success/error
    isComplete = true;

    console.log('[CheckoutHandler] 🚪 Checkout window closed by user');

    // Cleanup
    cleanup();

    // Call close callback with error handling
    if (onClose) {
      try {
        await onClose();
      } catch (error) {
        console.error('[CheckoutHandler] Error in onClose callback:', error);
      }
    }
  };

  // Open checkout in new tab
  try {
    checkoutWindow = window.open(checkoutUrl, '_blank');

    if (!checkoutWindow) {
      throw new Error('Failed to open checkout window. Please allow popups.');
    }

    console.log('[CheckoutHandler] Checkout window opened successfully');
  } catch (error: any) {
    console.error('[CheckoutHandler] Failed to open checkout:', error);
    handleError(error.message);
    return { cleanup, checkoutWindow: null };
  }

  // Monitor checkout window for manual close
  windowCheckInterval = setInterval(() => {
    if (checkoutWindow && checkoutWindow.closed) {
      console.log('[CheckoutHandler] Detected checkout window closed');
      handleWindowClose();
    }
  }, 1000); // Check every second

  // Listen for PostMessage from success/error pages
  messageListener = (event: MessageEvent) => {
    // Security: verify origin - same domain since both parent and success/error pages are on www.wlkthru.co
    if (event.origin !== window.location.origin) {
      console.warn('[CheckoutHandler] Ignoring message from unexpected origin:', event.origin);
      return;
    }

    console.log('[CheckoutHandler] Received message:', event.data);

    // Handle success
    if (event.data.type === 'STRIPE_CHECKOUT_SUCCESS') {
      handleSuccess();
    }

    // Handle error/cancel
    if (event.data.type === 'STRIPE_CHECKOUT_ERROR' || event.data.type === 'STRIPE_CHECKOUT_CANCEL') {
      handleError(event.data.error || 'Payment canceled');
    }
  };

  window.addEventListener('message', messageListener);

  return {
    cleanup,
    checkoutWindow,
  };
}
